import type { Locale } from './i18n';

/**
 * Channels that carry notifications beyond the app (ADR-0020). The in-app list always gets them.
 * The e-mail names title, module and link of each unread notification; Teams and push (ADR-0058)
 * only point to the app with a neutral text.
 */
export const NOTIFICATION_CHANNELS = ['email', 'teams', 'push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** E-mail of a company: through the SMTP server of the platform, an own one, or none. */
export const EMAIL_MODES = ['platform', 'own', 'off'] as const;
export type EmailMode = (typeof EMAIL_MODES)[number];

export const SMTP_SECURITY = ['tls', 'starttls'] as const;
export type SmtpSecurity = (typeof SMTP_SECURITY)[number];

/** Why sending failed, as the admin page and the profile name it. */
export const CHANNEL_ERRORS = [
  'not-configured',
  'no-address',
  'auth-failed',
  'connection-failed',
  'rejected',
] as const;
export type ChannelErrorCode = (typeof CHANNEL_ERRORS)[number];

/**
 * Devices a push subscription is shown as in the profile (ADR-0058). Only the kind of device, no
 * browser version or other details of the user agent.
 */
export const PUSH_DEVICES = [
  'iphone',
  'ipad',
  'mac',
  'windows',
  'android',
  'linux',
  'other',
] as const;
export type PushDevice = (typeof PUSH_DEVICES)[number];

/** Personal settings that switch a channel; push is switched per device instead. */
export const CHANNEL_SETTINGS = {
  email: 'core.emailNotifications',
  teams: 'core.teamsNotifications',
} as const satisfies Partial<Record<NotificationChannel, string>>;

/**
 * Titles of the notifications the kernel sends, below `core.`. The browser and the e-mail use the
 * same texts, so they live here and not only in the web translations.
 */
export const NOTIFICATION_TITLES = {
  de: {
    reminders: { notification: 'Eine Wiedervorlage ist fällig' },
    tasks: { assigned: 'Eine Aufgabe wurde zugewiesen', due: 'Eine Aufgabe ist fällig' },
    comments: {
      notification: 'Neuer Kommentar in einer Unterhaltung',
      mentioned: 'Erwähnung in einem Kommentar',
    },
    workflows: {
      approvalRequested: 'Neue Genehmigung wartet',
      decided: 'Über eine Anfrage wurde entschieden',
    },
  },
  en: {
    reminders: { notification: 'A reminder is due' },
    tasks: { assigned: 'A task was assigned to you', due: 'A task is due' },
    comments: {
      notification: 'New comment in a conversation',
      mentioned: 'Mentioned in a comment',
    },
    workflows: {
      approvalRequested: 'A new approval is waiting',
      decided: 'A request has been decided',
    },
  },
} satisfies Record<Locale, unknown>;
